import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { getApiCall, postApiCall } from "../../../service/httpCall";
import {
  API_SMSWEBHOOK_LIST,
  API_SMSWEBHOOK_DELETE,
} from "../../../service/url";
import { useAuth, errorCheck } from "../auth/auth-utils";
import { ROUTES, MODULE_ACL, USER_ROLES } from "../../../utilities/constant";
import ToastNotification from "../../../components/Toast";
import Loader from "../../../components/partials/components/loader";
import exportData from "../../../utilities/export";
import downloadTemplate from "../../../utilities/downloadCsv";
import CopyButton from "./components/CopyButton";
import { getUser } from "../../../utilities/setting";
import { toast } from "react-toastify";


const SmsWebhookList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [searchValue, setSearchValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [webhooksData, setWebhooksData] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTitle, setViewTitle] = useState("");
  const [viewContent, setViewContent] = useState("");
  const [viewIsJson, setViewIsJson] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState({})
  const [isSuperAdmin, setIsSuperadmin] = useState(false)

  //  Fetch list
  useEffect(() => {
    setLoading(true);
      getUser().then((user) => {
         if (user.role === USER_ROLES.SUPERADMIN) {
            setIsSuperadmin(true);
         }
         if (user?.acl_settings) {
            setLoggedInUserACL(JSON.parse(user.acl_settings.module_operations));
         }
      });
    getApiCall(API_SMSWEBHOOK_LIST, {})
      .then(onListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);
  const handleExport = async () => {
    setButtonDisable(true);
    await exportData("Sms_Webhooks", setToastProps, setShowToast);
    setButtonDisable(false);
  };

  const onListSuccess = (resp) => {
    setWebhooksData(resp);
  };

  const onError = (err) => {
    if (typeof err !== "object" && err.includes("TokenExpiredError")) {
      errorCheck(err, navigate, setToastProps, setShowToast);
      toast.error(err);
    } else {
      console.log(err);
    }
  };

  //   Delete webhook
  const confirmDelete = () => {
    postApiCall(API_SMSWEBHOOK_DELETE, { id: webhookToDelete })
      .then(onDeleteSuccess)
      .catch(onDeleteError);
    setShowDeleteModal(false);
    setWebhookToDelete(null);
  };

  const onDeleteSuccess = (resp) => {
    toast.success("Webhook deleted successfully");
    getApiCall(API_SMSWEBHOOK_LIST, {}).then(onListSuccess).catch(onError);
  };

  const onDeleteError = (err) => {
    toast.error(JSON.stringify(err));
  };

  const openViewModal = (content, title, isJson = false) => {
    setViewTitle(title);
    setViewContent(content ?? "");
    setViewIsJson(isJson);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewTitle("");
    setViewContent("");
    setViewIsJson(false);
  };

  const formatJson = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  //  Actions (edit/delete)
  const actionBodyTemplate = (rowData) => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
      }}
    >
       {(isSuperAdmin || loggedInUserACL[MODULE_ACL.SMS_WEBHOOK]?.edit) && (
        <FiEdit
          style={{ color: "#007bff", cursor: "pointer" }}
          onClick={() => handleAction("edit", rowData.id)}
        />
       )}
       {(isSuperAdmin || loggedInUserACL[MODULE_ACL.SMS_WEBHOOK]?.delete) && (
        <FiTrash2
          style={{ color: "#dc3545", cursor: "pointer" }}
          onClick={() => handleAction("delete", rowData.id)}
        />
       )}


    </div>
  );

  const handleAction = (action, id) => {
    if (action === "edit") {
      const url = ROUTES.SMSWEBHOOK.UPDATE.replace(":id", id);
      navigate(`/${url}`);
    } else if (action === "delete") {
      setWebhookToDelete(id);
      setShowDeleteModal(true);
    }
  };

  //  Filter search
  const filteredWebhooks = webhooksData.filter((item) =>
    item.webhook_name.toLowerCase().includes(searchValue.toLowerCase())
  );

  if (loading) return <Loader />;

  return (
    <>
      <div className="webhooks-container">
        <Row>
          <Col sm="12">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div className="header-title px-4 py-3 d-flex justify-content-between align-items-center">
                  <h4 className="card-title m-0">
                    Sms Webhooks ({webhooksData.length})
                  </h4>
                  <div className="d-flex gap-3">
                     {(isSuperAdmin || loggedInUserACL[MODULE_ACL.SMS_WEBHOOK]?.add) && (
                      <Button
                        variant="primary"
                        className="d-flex align-items-center"
                        onClick={() => navigate(`/${ROUTES.SMSWEBHOOK.CREATE}`)}
                      >
                        <FiPlus className="me-2" />
                        Create Sms Webhook
                      </Button>
                     )}
                    {webhooksData.length > 0 && (
                      <Button
                        label="Export Data"
                        icon="pi pi-file-export"
                        onClick={handleExport}
                        className="p-button-active"
                        style={{ paddingLeft: "20px" }}
                        disabled={buttonDisable}
                      >
                        Export Data &nbsp;
                        {buttonDisable && (
                          <span
                            className="spinner-border spinner-border-sm ml-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="px-4 py-3">
                  <Form className="d-flex align-items-center">
                    <Form.Control
                      placeholder="Search Sms Webhooks by name..."
                      type="text"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      style={{ maxWidth: "250px" }}
                    />
                  </Form>
                </div>

                <div className="pb-4">
                  <div style={{ overflowX: "auto" }} className="broad-scrollbar">
                    <DataTable
                      value={filteredWebhooks}
                      scrollable
                      tableStyle={{ minWidth: "100%" }}
                      paginator
                      rows={5}
                    >
                      <Column
                        field="id"
                        header="Webhook Id"
                        sortable
                        style={{ minWidth: "100px" }}
                      />
                      <Column
                        field="webhook_name"
                        header="Webhook Name"
                        sortable
                        style={{ minWidth: "200px" }}
                      />
                      <Column
                        field="sms_text"
                        header="SMS Text"
                        body={(row) =>
                          row.sms_text ? (
                            <Button
                              variant="link"
                              className="p-0"
                              onClick={() =>
                                openViewModal(row.sms_text, "SMS Text", false)
                              }
                            >
                              View SMS Text
                            </Button>
                          ) : (
                            "-"
                          )
                        }
                        style={{ minWidth: "250px" }}
                      />
                      <Column
                        field="payload"
                        header="Payload"
                        body={(row) =>
                          row.payload ? (
                            <Button
                              variant="link"
                              className="p-0"
                              onClick={() => {
                                const payloadStr =
                                  typeof row.payload === "string"
                                    ? row.payload
                                    : JSON.stringify(row.payload);
                                openViewModal(payloadStr, "Payload", true);
                              }}
                            >
                              View Payload
                            </Button>
                          ) : (
                            "-"
                          )
                        }
                        style={{
                          minWidth: "300px",
                        }}
                      />
                      <Column
                        field="header"
                        header="Headers"
                        body={(row) =>
                          row.header ? (
                            <Button
                              variant="link"
                              className="p-0"
                              onClick={() => {
                                const headerStr =
                                  typeof row.header === "string"
                                    ? row.header
                                    : JSON.stringify(row.header);
                                openViewModal(headerStr, "Headers", true);
                              }}
                            >
                              View Headers
                            </Button>
                          ) : (
                            "-"
                          )
                        }
                        style={{
                          minWidth: "220px",
                        }}
                      />

                      <Column
                        field="url"
                        header="Webhook Url"
                        sortable
                        body={(row) =>
                          row.url ? (
                            <Button
                              variant="link"
                              className="p-0"
                              onClick={() => openViewModal(row.url, "Webhook URL", false)}
                            >
                              View URL
                            </Button>
                          ) : (
                            "-"
                          )
                        }
                        style={{ minWidth: "300px" }}
                      />
                      <Column
                        field="request_type"
                        header="Request Type"
                        body={(row) =>
                          row.request_type === "WHATSAPP" ? "POST" : row.request_type
                        }
                        style={{ minWidth: "150px" }}
                      />
                      <Column
                        field="created_at"
                        header="Request Date"
                        body={(row) =>
                          row.created_at
                            ? new Date(row.created_at).toLocaleString()
                            : "-"
                        }
                        style={{ minWidth: "200px" }}
                        sortable
                      />
                      <Column
                        header="Action"
                        body={actionBodyTemplate}
                        style={{ width: "120px" }}
                      />
                    </DataTable>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Delete Modal */}
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this webhook? This action cannot be
            undone.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        {/* View Content Modal */}
        <Modal
          size="lg"
          show={showViewModal}
          onHide={closeViewModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{viewTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex justify-content-end mb-2">
              <CopyButton text={viewContent} />
            </div>
            <div
              style={{
                maxHeight: "60vh",
                overflow: "auto",
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "12px",
                background: "#fafafa",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
              className="broad-scrollbar"
            >
              {viewIsJson ? (
                <pre style={{ margin: 0 }}>{formatJson(viewContent)}</pre>
              ) : (
                <div>{viewContent}</div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeViewModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <style>
{`
  .broad-scrollbar::-webkit-scrollbar {
    height: 8px; 
    width: 8px;
  }
`}
</style>

      <ToastNotification
        show_toast={showToast}
        set_show_toast={setShowToast}
        {...toastProps}
      />
    </>
  );
};

export default SmsWebhookList;
